import React, { useState } from "react"
import Tooltip from "@mui/material/Tooltip"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import LinkIcon from "@mui/icons-material/Link"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import { Container, Draggable } from "react-smooth-dnd"
import { generateInnerciseLinkHtml } from "utils/program"
import { exchangeArrayItem } from "utils/data"
import { StyledProgramLevelCard } from "./Program.style"
import { INNERCISE_MODE } from "constants/global"
import {
  buildVariantSrcSet,
  getVariantSizes,
  selectPreferredVariantUrl,
} from "utils/imageVariants"

const ProgramLevelCard = ({
  programId,
  level,
  access,
  openProgramInnerciseDlg,
  openProgramLevelDlg,
  openDeleteLevelDlg,
  deleteProgramInnerciseAction,
  onInnerciseOrderChange,
}) => {
  const [showInnercise, setShowInnercise] = useState(false)

  // Reordering reuses the same permission that already guards level reordering.
  // Playbooks and innercises share the same index column so they are reordered together.
  const canDragInnercises =
    !!access?.exchangeLevelIndexAccess &&
    typeof onInnerciseOrderChange === "function" &&
    Array.isArray(level.innerciseList)

  const handleInnerciseDrop = (dropResult) => {
    const { addedIndex, removedIndex } = dropResult
    if (
      addedIndex == null ||
      removedIndex == null
    ) {
      if (addedIndex == null && removedIndex == null) {
        return
      }
      onInnerciseOrderChange(level.id, dropResult)
      return
    }
    if (addedIndex === removedIndex) {
      return
    }
    const reorderedInnerciseList = exchangeArrayItem({
      addedIndex,
      removedIndex,
      indexString: "innerciseIndex",
      list: level.innerciseList,
    })
    onInnerciseOrderChange(level.id, {
      ...dropResult,
      reorderedInnerciseList,
    })
  }

  const renderInnerciseCard = (innercise) => {
    const urlDto = innercise.thumbResourceDto?.urlDto
    const variantSrcSet = buildVariantSrcSet(urlDto?.variants)
    const variantSizes = getVariantSizes(variantSrcSet)
    const previewSrc =
      selectPreferredVariantUrl(urlDto?.variants) ||
      urlDto?.imgUrl ||
      urlDto?.originalUrl ||
      ""

    const typeLabel =
      innercise.dataType === INNERCISE_MODE.PLAYBOOK.value
        ? "Playbook"
        : "Innercise"

    return (
      <div
        className="innercise-item"
        key={`program-innercise-item-${innercise.id}`}
      >
        {canDragInnercises && (
          <Tooltip title="Drag to reorder or move to another level">
            <DragIndicatorIcon
              className="drag-handle"
              fontSize="small"
            />
          </Tooltip>
        )}

        <div className="thumb">
          {previewSrc ? (
            <img
              src={previewSrc}
              alt=""
              srcSet={variantSrcSet || undefined}
              sizes={variantSizes}
              loading="lazy"
            />
          ) : (
            <div className="thumb-empty" />
          )}
        </div>

        <div className="meta">
          <div className="id-label">
            {typeLabel} ID: <strong>{innercise.id}</strong>
          </div>
          <div className="title" title={innercise.title}>
            {innercise.title}
          </div>
        </div>

        <div className="icon-btn">
          {access.editInnerciseAccess && (
            <>
              <Tooltip title="Copy Deep Link Html">
                <IconButton
                  sx={{ color: "#182b06" }}
                  aria-label="copy innercise share link"
                  component="label"
                  onClick={async () => {
                    const htmlLink = generateInnerciseLinkHtml({
                      title: innercise.title,
                    })
                    await navigator.clipboard.writeText(htmlLink)
                  }}
                  size="small"
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit Innercise">
                <IconButton
                  sx={{ color: "#5d8f2d" }}
                  aria-label="edit innercise"
                  component="label"
                  onClick={() => {
                    openProgramInnerciseDlg(
                      innercise.dataType, // Innercise Mode: 1=Innercise, 2=Playbook
                      programId,
                      level.id,
                      innercise.id
                    )
                  }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}

          {access.deleteInnerciseAccess && (
            <Tooltip title="Delete Innercise">
              <IconButton
                sx={{ color: "#bb3030" }}
                aria-label="delete innercise"
                component="label"
                onClick={() => {
                  deleteProgramInnerciseAction(innercise.id)
                }}
                size="small"
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    )
  }

  return (
    <StyledProgramLevelCard>
      <div className="header">
        <div>
          {level.name}
          {level?.hideLevelLabel ? (
            <Chip
              size="small"
              color="default"
              label="Label hidden"
              title="This level's label is hidden in apps."
              sx={{ ml: 1 }}
            />
          ) : null}
        </div>
        <div>
          {level?.tierProgramId ? (
            <Chip
              color="primary"
              label={`Access Override`}
              title={`This level has its own access rules (tierProgramId=${level.tierProgramId}${level.productProgramId ? `, productProgramId=${level.productProgramId}` : ''}).`}
            />
          ) : (
            <Chip color="default" label="Inherits Program" />
          )}
          {access.editLevelAccess && (
            <EditIcon
              className="btn edit-btn"
              size="sm"
              onClick={() => {
                openProgramLevelDlg(level)
              }}
            />
          )}
          {access.deleteLevelAccess && (
            <CloseIcon
              className="btn close-btn"
              size="sm"
              role="button"
              onClick={() => {
                openDeleteLevelDlg(level.id)
              }}
            />
          )}
        </div>
      </div>

      <div className="innercise-header">
        <div className="label">
          {access.addInnerciseAccess && (
            <>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  openProgramInnerciseDlg(
                    INNERCISE_MODE.INNERCISE.value,
                    programId,
                    level.id,
                    null
                  )
                  setShowInnercise(true)
                }}
                size="small"
                style={{ marginRight: "10px" }}
              >
                Add Innercise
              </Button>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  openProgramInnerciseDlg(
                    INNERCISE_MODE.PLAYBOOK.value,
                    programId,
                    level.id,
                    null
                  )
                  setShowInnercise(true)
                }}
                size="small"
                style={{
                  marginRight: "10px",
                  backgroundColor: "rgb(93, 143, 45)",
                }}
              >
                Add Playbook
              </Button>
            </>
          )}
        </div>
        <div>
          <Button
            variant="contained"
            endIcon={showInnercise ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => {
              setShowInnercise(!showInnercise)
            }}
            size="small"
            color="success"
          >
            {showInnercise ? "Hide" : "Show"}
          </Button>
        </div>
      </div>

      <div className={`innercise-list ${!showInnercise && "hide"}`}>
        {canDragInnercises ? (
          <Container
            groupName="program-innercises"
            className="innercise-drop-zone"
            orientation="vertical"
            dragHandleSelector=".drag-handle"
            getChildPayload={(index) => ({
              ...level.innerciseList[index],
              sourceLevelId: level.id,
            })}
            dropPlaceholder={{
              animationDuration: 150,
              showOnTop: true,
              className: "innercise-drop-placeholder",
            }}
            onDrop={handleInnerciseDrop}
          >
            {level.innerciseList.map((innercise) => (
              <Draggable key={`program-innercise-draggable-${innercise.id}`}>
                {renderInnerciseCard(innercise)}
              </Draggable>
            ))}
          </Container>
        ) : (
          level.innerciseList.map((innercise) => renderInnerciseCard(innercise))
        )}
      </div>
    </StyledProgramLevelCard>
  )
}

export default ProgramLevelCard
